import { isZagruProblem } from '@zagru/platform';

/** `import.meta.env.DEV` when available (Vite / vitest), else false. */
export const isDev: boolean = (() => {
  try {
    return Boolean(import.meta.env?.DEV);
  } catch {
    return false;
  }
})();

/**
 * A heading for a failed widget or bound component.
 *
 * `problem.title` is the server's own phrase for a 4xx it raised on purpose and is worth showing; on a 5xx, a
 * transport failure or a thrown `TypeError` it is a framework string ("Internal Server Error") or an exception
 * class name, and the caller's fallback — which names the thing that failed — says more.
 */
export function errorTitle(error: unknown, fallback = 'Request failed'): string {
  if (isZagruProblem(error)) return error.status >= 400 && error.status < 500 && error.title ? error.title : fallback;
  return fallback;
}

/**
 * What went wrong, in words, with nothing support-shaped in it.
 *
 * It used to append `(outbound.wave_locked) ref 9f2c1e` to every message; the code and the correlation id now
 * render through `DiagnosticDetails` next to the message, collapsed, the way every other failure surface shows
 * them. A 5xx or a client-side failure keeps its `detail` off the screen entirely — that prose describes the
 * server's internals, not the reader's situation.
 */
export function errorDetail(error: unknown, tryAgain = 'Please try again. If it keeps happening, quote the diagnostic ID to support.'): string | undefined {
  if (isZagruProblem(error)) {
    const fromServer = error.status >= 400 && error.status < 500 && !error.code?.startsWith('client.') ? error.detail : undefined;
    return fromServer ?? tryAgain;
  }
  return tryAgain;
}

/** The `code` / `correlationId` / `status` a `DiagnosticDetails` needs, or nothing when the throw carried none. */
export function errorDiagnostics(error: unknown): { code?: string; correlationId?: string; status?: number } {
  if (!isZagruProblem(error)) return {};
  return { code: error.code, correlationId: error.correlationId, status: error.status };
}
